import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from 'react-modal';
import Select from 'react-select';
import styles from './UUTStatusSummaryChart.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import {
    fetchAteSwVersionOptions,
    fetchSerialNumberOptions,
    fetchTestspecOptions,
    fetchUutStatusSummary,
} from './uutStatusSummary/uutStatusSummaryApi';
import {
    buildStatusSummaryChartData,
    getSummaryMetrics,
    isPassOnlyRow,
    sortStatusSummaryRows,
    statusSummaryChartOptions,
} from './uutStatusSummary/uutStatusSummaryData';
import {
    exportStatusChartAsPNG,
    exportStatusTableAsCSV,
    exportStatusTableAsPDF,
} from './uutStatusSummary/uutStatusSummaryExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
Modal.setAppElement('#root');

const emptyFilters = {
    startDate: null,
    endDate: null,
    selectedSerialNumbers: [],
    selectedFamilies: [],
    ateSwVersion: '',
    selectedTestspec: '',
};

const UUTStatusSummaryChart = () => {
    const { t } = useTranslation();
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [serialNumbers, setSerialNumbers] = useState([]);
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const [ateSwVersions, setAteSwVersions] = useState([]);
    const [selectedAteSwVersion, setSelectedAteSwVersion] = useState('');
    const [testspecs, setTestspecs] = useState([]);
    const [selectedTestspec, setSelectedTestspec] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const chartRef = useRef(null);

    const currentFilters = useMemo(() => ({
        startDate,
        endDate,
        selectedSerialNumbers,
        selectedFamilies,
        ateSwVersion: selectedAteSwVersion,
        selectedTestspec,
    }), [endDate, selectedAteSwVersion, selectedFamilies, selectedSerialNumbers, selectedTestspec, startDate]);

    const chartData = useMemo(() => buildStatusSummaryChartData(summaryData), [summaryData]);
    const sortedTableData = useMemo(() => sortStatusSummaryRows(summaryData), [summaryData]);
    const metrics = useMemo(() => getSummaryMetrics(summaryData), [summaryData]);
    const hasData = summaryData.length > 0;

    const loadSummaryData = async (filters = currentFilters) => {
        setLoading(true);
        setError(null);

        try {
            const rows = await fetchUutStatusSummary(filters);
            setSummaryData(rows);
        } catch (fetchError) {
            console.error('Error fetching UUT status summary:', fetchError);
            setSummaryData([]);
            setError(t('Failed to load UUT status summary data. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const [serials, versions, specs] = await Promise.all([
                    fetchSerialNumberOptions(),
                    fetchAteSwVersionOptions(),
                    fetchTestspecOptions(),
                ]);
                setSerialNumbers(serials);
                setAteSwVersions(versions);
                setTestspecs(specs);
            } catch (metadataError) {
                console.error('Error fetching serial numbers:', metadataError);
                setError(t('Serial number filter options could not be loaded.'));
            }
        };

        loadMetadata();
        loadSummaryData(emptyFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApplyFilters = () => {
        loadSummaryData();
    };

    const resetFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedSerialNumbers([]);
        setSelectedFamilies([]);
        setSelectedAteSwVersion('');
        setSelectedTestspec('');
        loadSummaryData(emptyFilters);
    };

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>{t('Unit status')}</p>
                    <h2 className={styles.pageTitle}>{t('UUT Status Summary')}</h2>
                    <p className={styles.description}>
                        {t('Compare PASS, FAIL, STOP, and UNCOMPLETE records across serial numbers.')}
                    </p>
                </div>
                <button className={styles.helpButtonInline} onClick={() => setIsHelpOpen(true)}>
                    {t('Page help')}
                </button>
            </header>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <section className={styles.filters}>
                <div className={styles.filterHeader}>
                    <div>
                        <h3>{t('Filters')}</h3>
                        <p>{t('Limit the summary by date range and serial numbers.')}</p>
                    </div>
                    <button onClick={resetFilters} className={styles.resetFilterButton}>
                        {t('Reset')}
                    </button>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.datePickerContainer}>
                        <label>{t('Start date')}</label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText={t('Any start date')}
                            className={styles.datePicker}
                            isClearable
                        />
                    </div>

                    <div className={styles.datePickerContainer}>
                        <label>{t('End date')}</label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText={t('Any end date')}
                            className={styles.datePicker}
                            isClearable
                            minDate={startDate}
                        />
                    </div>

                    <div className={`${styles.dropdownContainer} ${styles.serialFilter}`}>
                        <label>{t('Serial numbers')}</label>
                        <Select
                            isMulti
                            options={serialNumbers}
                            value={selectedSerialNumbers}
                            onChange={(values) => setSelectedSerialNumbers(values || [])}
                            placeholder={t('All serial numbers')}
                            className={styles.multiSelect}
                            classNamePrefix="summarySelect"
                        />
                    </div>

                    <div className={styles.dropdownContainer}>
                        <label>{t('ATE SW version')}</label>
                        <select
                            value={selectedAteSwVersion}
                            onChange={(event) => setSelectedAteSwVersion(event.target.value)}
                            className={styles.datePicker}
                        >
                            <option value="">{t('All versions')}</option>
                            {ateSwVersions.map((version) => (
                                <option key={version.value} value={version.value}>{version.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.dropdownContainer}>
                        <label>{t('Testspec')}</label>
                        <select
                            value={selectedTestspec}
                            onChange={(event) => setSelectedTestspec(event.target.value)}
                            className={styles.datePicker}
                        >
                            <option value="">{t('All testspecs')}</option>
                            {testspecs.map((spec) => (
                                <option key={spec.value} value={spec.value}>{spec.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.filterFooter}>
                    <span className={styles.filterHint}>
                        {selectedSerialNumbers.length
                            ? t('{{count}} serial number selected', { count: selectedSerialNumbers.length })
                            : t('Showing all serial numbers')}
                    </span>
                    <button onClick={handleApplyFilters} className={styles.applyFilterButton}>
                        {t('Apply filters')}
                    </button>
                </div>

                <SourceFileSummary
                    compact
                    startDate={startDate}
                    endDate={endDate}
                    ateSwVersion={selectedAteSwVersion}
                    testspec={selectedTestspec}
                    serialNumbers={selectedSerialNumbers}
                    selectedFamilies={selectedFamilies}
                    onFamiliesChange={setSelectedFamilies}
                    title={t('Source files used for dashboard charts')}
                />
            </section>

            <section className={styles.metricsGrid}>
                {metrics.map((metric) => (
                    <div className={`${styles.metricCard} ${styles[metric.tone]}`} key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                    </div>
                ))}
            </section>

            <section className={styles.chartPanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>{t('Status counts by serial number')}</h3>
                        <p>{t('Grouped bars show the count of each status for every serial number.')}</p>
                    </div>
                    <button onClick={() => setIsModalOpen(true)} className={styles.expandButton} disabled={!hasData}>
                        {t('Expand chart')}
                    </button>
                </div>

                <div className={styles.chartContainer} ref={chartRef}>
                    {loading ? (
                        <div className={styles.loadingScreen}>
                            <div className={styles.spinner}></div>
                            <p>{t('Loading summary data...')}</p>
                        </div>
                    ) : hasData ? (
                        <Bar data={chartData} options={statusSummaryChartOptions} />
                    ) : (
                        <div className={styles.emptyState}>{t('No status summary data found for these filters.')}</div>
                    )}
                </div>
            </section>

            <section className={styles.tablePanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>{t('Summary table')}</h3>
                        <p>{t('Pass-only rows are highlighted to make healthy units easier to scan.')}</p>
                    </div>
                    <div className={styles.exportButtons}>
                        <button onClick={() => exportStatusChartAsPNG(chartRef.current)} className={styles.applyFilterButton} disabled={!hasData}>
                            {t('Chart PNG')}
                        </button>
                        <button onClick={() => setShowTable((visible) => !visible)} className={styles.viewTableButton}>
                            {showTable ? t('Hide table') : t('Show table')}
                        </button>
                        <button onClick={() => exportStatusTableAsCSV(sortedTableData)} className={styles.applyFilterButton} disabled={!showTable || !hasData}>
                            CSV
                        </button>
                        <button onClick={() => exportStatusTableAsPDF(sortedTableData)} className={styles.applyFilterButton} disabled={!showTable || !hasData}>
                            PDF
                        </button>
                    </div>
                </div>

                {showTable && (
                    <div className={styles.tableWrap}>
                        <table className={styles.styledTable}>
                            <thead>
                                <tr>
                                    <th className={styles.styledTableTh}>#</th>
                                    <th className={styles.styledTableTh}>{t('Serial number')}</th>
                                    <th className={styles.styledTableTh}>{t('PASS count')}</th>
                                    <th className={styles.styledTableTh}>{t('FAIL count')}</th>
                                    <th className={styles.styledTableTh}>{t('STOP count')}</th>
                                    <th className={styles.styledTableTh}>{t('UNCOMPLETE count')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTableData.map((data, index) => (
                                    <tr
                                        key={data.serialNumber}
                                        className={isPassOnlyRow(data) ? styles.passOnlyRow : ''}
                                    >
                                        <td className={styles.styledTableTd}>{index + 1}</td>
                                        <td className={styles.styledTableTd}>{data.serialNumber}</td>
                                        <td className={styles.styledTableTd}>{data.passCount}</td>
                                        <td className={styles.styledTableTd}>{data.failCount}</td>
                                        <td className={styles.styledTableTd}>{data.stopCount}</td>
                                        <td className={styles.styledTableTd}>{data.uncompleteCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <Modal
                isOpen={isModalOpen}
                onRequestClose={() => setIsModalOpen(false)}
                className={styles.modalContent}
                overlayClassName={styles.modalOverlay}
                contentLabel={t('Expanded UUT Status Summary Chart')}
            >
                <button onClick={() => setIsModalOpen(false)} className={styles.closeButton}>{t('Close')}</button>
                <div className={styles.expandedChartContainer}>
                    <Bar data={chartData} options={statusSummaryChartOptions} />
                </div>
            </Modal>

            <Modal
                isOpen={isHelpOpen}
                onRequestClose={() => setIsHelpOpen(false)}
                className={styles.helpModalContent}
                overlayClassName={styles.helpModalOverlay}
                contentLabel={t('Help Information')}
            >
                <button onClick={() => setIsHelpOpen(false)} className={styles.closeHelpButton}>
                    {t('Close')}
                </button>
                <div className={styles.helpText}>
                    <h2>{t('About this page')}</h2>
                    <p>
                        {t('This page groups uploaded test records by serial number and counts how many PASS, FAIL, STOP, and UNCOMPLETE results each unit has.')}
                    </p>
                    <p>
                        {t('Use the filters to focus on a date range or specific serial numbers, then export the chart or table for reports.')}
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default UUTStatusSummaryChart;
